// src/points/ManagePoints.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  // Tooltip,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  LinearProgress,
  Divider,
  // Stack,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
} from '@mui/material';
import {
  FaCoins,
  FaGift,
  FaHistory,
  FaPlus,
  FaEdit,
  FaTrash,
  // FaEye,
  FaCheckCircle,
  FaClock,
  FaBan,
  FaExchangeAlt,
  FaUsers,
  // FaChartLine,
  FaTrophy,
  FaWallet,
  // FaTree,
  // FaRecycle,
  // FaWater,
  FaRedo,
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { usePoints } from './PointsContext';
import SimpleTiptapEditor from '../components/tiptap-templates/simple/custom-editor';
import ReadOnlyEditor from '../components/tiptap-templates/simple/read-only-editor';

// const MotionPaper = motion(Paper);

export default function ManagePoints() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Get points context
  const {
    pointsSummary,
    allHistory,
    prizes,
    loading,
    getAllPrizesAdmin,
    getSystemSummary,
    getAllHistoryAdmin,
    getAllUsersPoints,
    addPrize,
    updatePrize,
    deletePrize,
    togglePrizeStatus,
  } = usePoints();

  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'add', 'edit', 'delete'
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [localPrizes, setLocalPrizes] = useState([]);
  const [localSummary, setLocalSummary] = useState({
    totalEarned: 0,
    totalRedeemable: 0,
    totalRedeemed: 0,
    totalPending: 0,
    totalUsers: 0,
    averagePoints: 0,
  });
  const [localHistory, setLocalHistory] = useState([]);
  
  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    points_required: '',
    stock: '',
    category: 'Environmental',
    type: 'prize',
    is_active: true,
    color: '#2e7d32',
  });

  // Load data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Update local state when context data changes
  useEffect(() => {
    if (prizes && prizes.length > 0) {
      setLocalPrizes(prizes);
    }
  }, [prizes]);

  useEffect(() => {
    if (pointsSummary) {
      setLocalSummary(pointsSummary);
    }
  }, [pointsSummary]);

  useEffect(() => {
    if (allHistory && allHistory.length > 0) {
      setLocalHistory(allHistory);
    }
  }, [allHistory]);

  const loadAllData = async () => {
    await getAllPrizesAdmin();
    await getSystemSummary();
    await getAllHistoryAdmin(100, 0);
    await getAllUsersPoints();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleRefresh = useCallback(async () => {
    await getAllPrizesAdmin()
    await getSystemSummary()
    await getAllHistoryAdmin()
}, [getAllPrizesAdmin, getSystemSummary,getAllHistoryAdmin]);

useEffect(() => {
  const interval = setInterval(() => {
    handleRefresh();
  }, 4 * 60 * 1000);

  return () => clearInterval(interval);
}, [handleRefresh]);

  const handleAddPrize = () => {
    setDialogType('add');
    setSelectedPrize(null);
    setFormData({
      name: '',
      description: '',
      points_required: '',
      stock: '',
      category: 'Environmental',
      type: 'prize',
      is_active: true,
      color: '#2e7d32',
    });
    setDialogOpen(true);
  };

  const handleEditPrize = (prize) => {
    setDialogType('edit');
    setSelectedPrize(prize);
    setFormData({
      name: prize.name,
      description: prize.description,
      points_required: prize.points_required,
      stock: prize.stock,
      category: prize.category,
      type: prize.type,
      is_active: prize.is_active,
      color: prize.color,
    });
    setDialogOpen(true);
  };

  const handleDeletePrize = (prize) => {
    setDialogType('delete');
    setSelectedPrize(prize);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedPrize(null);
    setFormData({
      name: '',
      description: '',
      points_required: '',
      stock: '',
      category: 'Environmental',
      type: 'prize',
      is_active: true,
      color: '#2e7d32',
    });
  };

  const handleFormChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const handleSavePrize = async () => {
    const prizeData = {
      name: formData.name,
      description: formData.description,
      points_required: parseInt(formData.points_required),
      stock: parseInt(formData.stock),
      category: formData.category,
      type: formData.type,
      is_active: formData.is_active,
      color: formData.color,
      gradient: `linear-gradient(135deg, ${formData.color}, ${formData.color}cc)`,
    };

    let result;
    if (dialogType === 'add') {
      result = await addPrize(prizeData);
      if (result?.success) {
        setSnackbar({
          open: true,
          message: 'Prize added successfully!',
          severity: 'success',
        });
      }
    } else {
      // console.log(selectedPrize,selectedPrize.id)
      result = await updatePrize(selectedPrize.id, prizeData);
      if (result?.success) {
        setSnackbar({
          open: true,
          message: 'Prize updated successfully!',
          severity: 'success',
        });
      }
    }
    
    if (result?.success) {
      await loadAllData();
      setDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    const result = await deletePrize(selectedPrize.id);
    if (result?.success) {
      setSnackbar({
        open: true,
        message: 'Prize deleted successfully!',
        severity: 'success',
      });
      await loadAllData();
      setDialogOpen(false);
    }
  };

  const handleToggleStatus = async (prizeId) => {
    const result = await togglePrizeStatus(prizeId);
    if (result?.success) {
      setSnackbar({
        open: true,
        message: 'Prize status updated!',
        severity: 'success',
      });
      await loadAllData();
    }
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'verified': return '#43a047';
      case 'pending': return '#ff9800';
      case 'spammed': return '#f44336';
      case 'redeemed': return '#7b1fa2';
      default: return '#666';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'verified': return <FaCheckCircle />;
      case 'pending': return <FaClock />;
      case 'spammed': return <FaBan />;
      case 'redeemed': return <FaExchangeAlt />;
      default: return <FaClock />;
    }
  };

  const getCategoryLabel = (category) => {
    switch(category) {
      case 'verified': return 'Verified';
      case 'pending': return 'Pending';
      case 'spammed': return 'Spammed';
      case 'redeemed': return 'Redeemed';
      default: return category;
    }
  };

  if (loading && localPrizes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#2e7d32' }} />
      </Box>
    );
  }

  const handleEditorChange = (htmlContent) => {
    handleFormChange(htmlContent);
    setFormData({
      ...formData,
      description: htmlContent,
    });
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(0,40,15,0.15), rgba(0,60,25,0.10), rgba(0,40,15,0.15))',
      backdropFilter: 'blur(2px)',
      py: isMobile ? 2 : 4,
      px: isMobile ? 1 : 2,
    }}>
      <Box maxWidth="xl" sx={{ mx: 'auto', px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
<Box
  mb={4}
  sx={{
    position: 'relative',
  }}
>
  <Typography
    variant={isMobile ? "h5" : "h4"}
    fontWeight="bold"
    color="#0ec617"
    gutterBottom
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      pr: 6,
    }}
  >
    <FaCoins
      size={isMobile ? 24 : 32}
      color="#0ec617"
    />
    Manage Points
  </Typography>

  <Typography
    variant="body2"
    color="#a6ffaa"
  >
    Overview of all points, history, and prize management
  </Typography>

  {/* Refresh */}
  <IconButton
    onClick={handleRefresh}
    aria-label="Refresh"
    sx={{
      position: 'absolute',
      top: 0,
      right: 0,
      color: '#0ec617',
      bgcolor: 'rgba(21, 255, 32, 0.28)',
      border: '1px solid',
      borderColor: 'rgba(46, 125, 50, 0.15)',
      width: 40,
      height: 40,
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: 'rgba(46, 125, 50, 0.12)',
        transform: 'rotate(90deg)',
      },
    }}
  >
    <FaRedo size={27} />
  </IconButton>
</Box>

        {/* Summary Cards */}
        <Grid container spacing={isMobile ? 2 : 3} justifyContent="center">
          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                borderRadius: '16px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
                height: '100%',
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, mx: 'auto', mb: 1 }}>
                <FaTrophy size={24} color="#a5d6a7" />
              </Avatar>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                {localSummary.totalEarned || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Total Points Earned
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                borderRadius: '16px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #01579b, #0277bd)',
                height: '100%',
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, mx: 'auto', mb: 1 }}>
                <FaWallet size={24} color="#a5d6a7" />
              </Avatar>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                {localSummary.totalRedeemable || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Redeemable Points
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                borderRadius: '16px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #4a148c, #6a1b9a)',
                height: '100%',
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, mx: 'auto', mb: 1 }}>
                <FaExchangeAlt size={24} color="#ce93d8" />
              </Avatar>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                {localSummary.totalRedeemed || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Points Redeemed
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                borderRadius: '16px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #e65100, #f57c00)',
                height: '100%',
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, mx: 'auto', mb: 1 }}>
                <FaClock size={24} color="#ffcc80" />
              </Avatar>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                {localSummary.totalPending || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Pending Points
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2.4}>
            <Paper
              elevation={3}
              sx={{
                p: isMobile ? 2 : 3,
                borderRadius: '16px',
                textAlign: 'center',
                background: 'linear-gradient(135deg, #00695c, #00897b)',
                height: '100%',
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, mx: 'auto', mb: 1 }}>
                <FaUsers size={24} color="#80cbc4" />
              </Avatar>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                {localSummary.totalUsers || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Active Users
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ 
          mt: 4, 
          borderBottom: 2, 
          borderColor: 'rgba(46,125,50,0.2)',
          bgcolor: 'rgba(255,255,255,0.7)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px 12px 0 0',
          overflow: 'hidden',
        }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant={isMobile ? "fullWidth" : "standard"}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: isMobile ? '0.8rem' : '0.95rem',
                minHeight: isMobile ? 48 : 64,
                '&.Mui-selected': {
                  color: '#2e7d32',
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#2e7d32',
                height: 3,
              },
            }}
          >
            <Tab icon={<FaHistory size={isMobile ? 14 : 18} />} label="All History" iconPosition="start" />
            <Tab icon={<FaGift size={isMobile ? 14 : 18} />} label="Manage Prizes" iconPosition="start" />
            <Tab icon={<FaCoins size={isMobile ? 14 : 18} />} label="All Points" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        {tabValue === 0 && (
          <Box mt={3}>
            <Paper sx={{ 
              borderRadius: '16px', 
              overflow: 'hidden',
              bgcolor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(10px)',
            }}>
              {localHistory.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(46,125,50,0.05)' }}>
                        <TableCell><Typography fontWeight={600}>User</Typography></TableCell>
                        <TableCell><Typography fontWeight={600}>Description</Typography></TableCell>
                        <TableCell><Typography fontWeight={600}>Category</Typography></TableCell>
                        <TableCell align="center"><Typography fontWeight={600}>Points</Typography></TableCell>
                        <TableCell align="center"><Typography fontWeight={600}>Date</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {localHistory.map((item) => (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>{item.user_id || 'N/A'}</Typography>
                              {/* <Typography variant="caption" color="text.secondary">{item.email || ''}</Typography> */}
                            </Box>
                          </TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={getCategoryIcon(item.category)}
                              label={getCategoryLabel(item.category)}
                              sx={{
                                bgcolor: `${getCategoryColor(item.category)}15`,
                                color: getCategoryColor(item.category),
                                '& .MuiChip-icon': {
                                  color: getCategoryColor(item.category),
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              fontWeight="bold"
                              sx={{ color: item.points > 0 ? '#43a047' : '#f44336' }}
                            >
                              {item.points > 0  ? '+' : ''}{item.points}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="caption">
                              {new Date(item.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No history records found
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {tabValue === 1 && (
          <Box mt={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
              <Typography variant="body2" color="text.secondary">
                Manage all available prizes and rewards ({localPrizes.length} prizes)
              </Typography>
              <Button
                variant="contained"
                startIcon={<FaPlus />}
                onClick={handleAddPrize}
                sx={{
                  bgcolor: '#2e7d32',
                  '&:hover': { bgcolor: '#1b5e20' },
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Add Prize
              </Button>
            </Box>

            <Grid container spacing={3}>
              {!!localPrizes && localPrizes
                .sort((a, b) => Number(b.is_active) - Number(a.is_active))
                .map((prize) => (
                <Grid item xs={12} sm={6} md={4} key={prize.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    layout
                  >
                    <Card
                      sx={{
                        borderRadius: '16px',
                        height: '100%',
                        position: 'relative',
                        bgcolor: prize.is_active ? 'rgba(255,255,255,0.9)' : 'rgba(250,250,250,0.8)',
                        backdropFilter: 'blur(10px)',
                        opacity: prize.is_active ? 1 : 0.6,
                        transition: 'all 0.3s ease',
                        border: `1px solid ${prize.is_active ? prize.color : '#e0e0e0'}20`,
                        '&:hover': {
                          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                          transform: 'translateY(-4px)',
                        },
                      }}
                    >
                      {!prize.is_active && (
                        <Chip
                          label="Inactive"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            fontWeight: 'bold',
                            zIndex: 1,
                            bgcolor: '#f44336',
                            color: '#fff',
                            '& .MuiChip-label': { fontWeight: 700 },
                          }}
                        />
                      )}
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={2}>
                          <Avatar sx={{ 
                            background: prize.gradient || prize.color,
                            width: 48, 
                            height: 48,
                            boxShadow: `0 4px 12px ${prize.color}40`,
                          }}>
                            {prize.icon || <FaGift size={24} color="#fff" />}
                          </Avatar>
                          <Box flex={1}>
                            <Typography variant="h6" fontWeight={600} noWrap>
                              {prize.name}
                            </Typography>
                            <Chip
                              label={prize.category}
                              size="small"
                              sx={{ 
                                bgcolor: prize.type === 'cash' ? 'rgba(230,81,0,0.1)' : 'rgba(46,125,50,0.1)',
                                color: prize.type === 'cash' ? '#e65100' : '#2e7d32',
                                fontSize: '0.6rem',
                                height: 20,
                              }}
                            />
                          </Box>
                        </Box>

                        {/* <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          mb={2}
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            minHeight: 40,
                          }}
                        >
                          {prize.description}
                        </Typography> */}
                        <Box 
                          sx={{ width: '100%',minWidth: '25vw', mb: 1.5,
                            minHeight: { xs: 54, sm: 58 },
                            // maxWidth: { xs: 58, sm: 62 },
                              overflow: 'hidden',
                              color: 'text.secondary',
                                fontSize: '0.875rem',
                                lineHeight: 1.55,
                                  '& p': { margin: 0, },
                                  '& ul, & ol': { margin: '4px 0',
                                    paddingLeft: '20px', }, 
                                    '& strong': { color: 'text.primary', },
                                    display: '-webkit-box',
                                    //  WebkitLineClamp: 3,
                                    //   WebkitBoxOrient: 'vertical',
                                    //    textOverflow: 'ellipsis',
                                        }}>
  
                          <ReadOnlyEditor content={prize.description}/>    
                        </Box>
                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Points Required</Typography>
                            <Typography variant="h6" fontWeight="bold" color="#2e7d32">
                              {prize.points_required} pts
                            </Typography>
                          </Box>
                          <Box textAlign="right">
                            <Typography variant="caption" color="text.secondary">Stock</Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {prize.stock}
                            </Typography>
                          </Box>
                        </Box>

                        <FormControlLabel
                          control={
                            <Switch
                              checked={prize.is_active}
                              onChange={() => handleToggleStatus(prize.id)}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                  color: '#2e7d32',
                                  '&:hover': { bgcolor: 'rgba(46,125,50,0.08)' },
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                  bgcolor: '#2e7d32',
                                },
                              }}
                            />
                          }
                          label={prize.is_active ? 'Active' : 'Inactive'}
                          sx={{ mt: 1 }}
                        />
                      </CardContent>
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        <Button
                          size="small"
                          startIcon={<FaEdit />}
                          onClick={() => handleEditPrize(prize)}
                          sx={{ 
                            color: '#2e7d32',
                            '&:hover': { bgcolor: 'rgba(46,125,50,0.08)' },
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<FaTrash />}
                          onClick={() => handleDeletePrize(prize)}
                          sx={{ 
                            color: '#f44336',
                            '&:hover': { bgcolor: 'rgba(244,67,54,0.08)' },
                          }}
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {tabValue === 2 && (
          <Box mt={3}>
            <Paper sx={{ 
              borderRadius: '16px', 
              overflow: 'hidden',
              bgcolor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(10px)',
              p: 4,
              textAlign: 'center',
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                All Points Overview
              </Typography>
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, bgcolor: 'rgba(46,125,50,0.05)', borderRadius: '12px' }}>
                    <Typography variant="body2" color="text.secondary">Total Points in System</Typography>
                    <Typography variant="h3" fontWeight="bold" color="#2e7d32">
                      {(localSummary.totalEarned || 0) + (localSummary.totalRedeemed || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, bgcolor: 'rgba(2,119,189,0.05)', borderRadius: '12px' }}>
                    <Typography variant="body2" color="text.secondary">Redeemable Points</Typography>
                    <Typography variant="h3" fontWeight="bold" color="#0277bd">
                      {localSummary.totalRedeemable || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 3, bgcolor: 'rgba(245,124,0,0.05)', borderRadius: '12px' }}>
                    <Typography variant="body2" color="text.secondary">Redeemed Points</Typography>
                    <Typography variant="h3" fontWeight="bold" color="#f57c00">
                      {localSummary.totalRedeemed || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '12px' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Points Distribution
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="caption" fontWeight={500}>Redeemable</Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {localSummary.totalRedeemable || 0} pts
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={((localSummary.totalRedeemable || 0) / ((localSummary.totalRedeemable || 0) + (localSummary.totalRedeemed || 0))) * 100 || 0}
                        sx={{ 
                          height: 10, 
                          borderRadius: 5,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#0277bd',
                          },
                        }}
                      />
                      <Box display="flex" justifyContent="space-between" mt={2} mb={1}>
                        <Typography variant="caption" fontWeight={500}>Redeemed</Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {localSummary.totalRedeemed || 0} pts
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={((localSummary.totalRedeemed || 0) / ((localSummary.totalRedeemable || 0) + (localSummary.totalRedeemed || 0))) * 100 || 0}
                        sx={{ 
                          height: 10, 
                          borderRadius: 5,
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#43a047',
                          },
                        }}
                      />
                      <Box display="flex" justifyContent="space-between" mt={2}>
                        <Typography variant="caption" color="text.secondary">
                          Total: {(localSummary.totalRedeemable || 0) + (localSummary.totalRedeemed || 0)} pts
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(((localSummary.totalRedeemable || 0) / ((localSummary.totalRedeemable || 0) + (localSummary.totalRedeemed || 0))) * 100) || 0}% Available
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Add/Edit Dialog */}
        <Dialog
          open={dialogOpen && dialogType !== 'delete'}
          onClose={handleDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: dialogType === 'add' ? 'rgba(46,125,50,0.05)' : 'rgba(2,119,189,0.05)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              {dialogType === 'add' ? (
                <Avatar sx={{ bgcolor: '#2e7d32', width: 32, height: 32 }}>
                  <FaPlus size={16} color="#fff" />
                </Avatar>
              ) : (
                <Avatar sx={{ bgcolor: '#0277bd', width: 32, height: 32 }}>
                  <FaEdit size={16} color="#fff" />
                </Avatar>
              )}
              <Typography variant="h6" fontWeight="bold">
                {dialogType === 'add' ? 'Add New Prize' : 'Edit Prize'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Box>
              <TextField
                fullWidth
                label="Prize Name"
                value={formData.name}
                onChange={handleFormChange('name')}
                sx={{ mb: 2 }}
              />
              {/* <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleFormChange('description')}
                sx={{ mb: 2 }}
              /> */}
              {/* <SimpleTiptapEditor setContent={handleFormChange('description')} content={formData.description} /> */}

              <SimpleTiptapEditor
                setContent={handleEditorChange}
                initialContent={formData.description }
                navbarHeight={0}
                debounceDelay={300}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Points Required"
                    type="number"
                    value={formData.points_required}
                    onChange={handleFormChange('points_required')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Stock"
                    type="number"
                    value={formData.stock}
                    onChange={handleFormChange('stock')}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      label="Category"
                      value={formData.category}
                      onChange={handleFormChange('category')}
                    >
                      <MenuItem value="Environmental">Environmental</MenuItem>
                      <MenuItem value="Merchandise">Merchandise</MenuItem>
                      <MenuItem value="Cash">Cash</MenuItem>
                      <MenuItem value="Community">Community</MenuItem>
                      <MenuItem value="Technology">Technology</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      label="Type"
                      value={formData.type}
                      onChange={handleFormChange('type')}
                    >
                      <MenuItem value="prize">Prize</MenuItem>
                      <MenuItem value="cash">Cash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange('is_active')}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2e7d32',
                        '&:hover': { bgcolor: 'rgba(46,125,50,0.08)' },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#2e7d32',
                      },
                    }}
                  />
                }
                label={formData.is_active ? 'Active' : 'Inactive'}
                sx={{ mt: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <Button onClick={handleDialogClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePrize}
              disabled={!formData.name || !formData.points_required || !formData.stock || loading}
              sx={{
                bgcolor: '#2e7d32',
                '&:hover': { bgcolor: '#1b5e20' },
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : (dialogType === 'add' ? 'Add Prize' : 'Save Changes')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog
          open={dialogOpen && dialogType === 'delete'}
          onClose={handleDialogClose}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
            }
          }}
        >
          <DialogTitle sx={{ 
            bgcolor: 'rgba(244,67,54,0.05)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ bgcolor: '#f44336', width: 32, height: 32 }}>
                <FaTrash size={16} color="#fff" />
              </Avatar>
              <Typography variant="h6" fontWeight="bold" color="#f44336">
                Delete Prize
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>
              Are you sure you want to delete <strong>"{selectedPrize?.name}"</strong>?
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This action cannot be undone. All associated data will be permanently removed.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
            <Button onClick={handleDialogClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600, px: 4 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete Prize'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.severity} 
            variant="filled" 
            sx={{ 
              borderRadius: '12px',
              '& .MuiAlert-icon': {
                fontSize: '1.2rem',
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}